import { Controller } from "@hotwired/stimulus";
import { get } from "@rails/request.js";

// Refreshes the POS-specific fields on the cross-POS integration form when
// either replication group select changes. Administrate selectizes these
// selects, and selectize fires only jQuery-level change events (invisible to
// Stimulus actions), so we bind to each selectize instance directly once it
// initializes (same waiting trick as contact_upload_batches_controller).
export default class extends Controller {
  static values = { path: String, id: String };

  connect() {
    this.pollIntervals = [];
    this.groupSelects().forEach((select) => {
      // a Turbo cache restore keeps the marker attribute but not the
      // listener; clear it so it only ever means "bound in this lifecycle"
      delete select.dataset.crossPosFieldsBound;
      this.waitForSelectize(select).then((selectize) => {
        selectize.on("change", () => this.refresh());
        // observable bind marker so tests can wait for the listener
        select.dataset.crossPosFieldsBound = "true";
      });
    });
  }

  disconnect() {
    // navigating away before selectize initializes must not leave pollers
    this.pollIntervals.forEach((interval) => clearInterval(interval));
    this.pollIntervals = [];
    this.pendingRefresh?.abort();
  }

  refresh() {
    const params = new URLSearchParams({
      primary_pos_replication_group_id: this.selectValue(
        "primary_pos_replication_group_id",
      ),
      secondary_pos_replication_group_id: this.selectValue(
        "secondary_pos_replication_group_id",
      ),
    });
    if (this.idValue) {
      params.set("id", this.idValue);
    }

    // request.js renders each stream as it lands, so a slow earlier refresh would win
    this.pendingRefresh?.abort();
    this.pendingRefresh = new AbortController();
    get(`${this.pathValue}?${params}`, {
      responseKind: "turbo-stream",
      signal: this.pendingRefresh.signal,
    }).catch((error) => {
      if (error.name !== "AbortError") throw error;
    });
  }

  groupSelects() {
    return [
      "primary_pos_replication_group_id",
      "secondary_pos_replication_group_id",
    ]
      .map((attribute) => this.groupSelect(attribute))
      .filter(Boolean);
  }

  groupSelect(attribute) {
    return this.element.querySelector(
      `[name="cross_pos_integration[${attribute}]"]`,
    );
  }

  selectValue(attribute) {
    return this.groupSelect(attribute)?.value ?? "";
  }

  waitForSelectize(select) {
    return new Promise((resolve) => {
      let attempts = 0;
      const interval = setInterval(() => {
        if (select.selectize) {
          clearInterval(interval);
          resolve(select.selectize);
        } else if (attempts++ > 100) {
          // selectize never initialized; give up rather than poll forever
          clearInterval(interval);
        }
      }, 100);
      this.pollIntervals.push(interval);
    });
  }
}
